import type { BenchmarkSelection, CompanySlug, ModelView, QualifiedModelScore } from "../shared/contracts.ts";
import { resolveModelScore } from "../shared/benchmark-observation.ts";
import { modelColorFamily, type LandscapeColoringMode, type ModelColorFamily, type ModelColorScheme } from "./model-coloring.ts";
import {
  effectivePricing,
  priceBandFor,
  type EffectivePricing,
  type PriceBandId,
  type PriceMode,
  type PricingActivity,
  type ScoreMetric,
  type TierScheme,
} from "./pricing.ts";

export interface LandscapePoint {
  id: string;
  x: number;
  y: number;
}

/** Keep the ring visible; central rings use vertical space outside the plot. */
export function placeRingChooser({ ring, width: preferredWidth, height: preferredHeight, chartWidth, visibleTop, visibleBottom }: {
  ring: { x: number; y: number }; width: number; height: number; chartWidth: number; visibleTop: number; visibleBottom: number;
}) {
  const gap = LANDSCAPE_CLUSTER_MARKER_BOUND + 16;
  const sideWidth = Math.max(ring.x, chartWidth - ring.x) - gap - 8;
  const width = sideWidth >= 200 ? Math.min(preferredWidth, sideWidth) : preferredWidth;
  const side = ring.x + gap + width <= chartWidth - 8 ? "right"
    : ring.x - gap - width >= 8 ? "left"
      : visibleBottom - ring.y >= preferredHeight + gap || visibleBottom - ring.y >= ring.y - visibleTop ? "below" : "above";
  const vertical = side === "above" || side === "below";
  const availableHeight = vertical ? (side === "above" ? ring.y - visibleTop : visibleBottom - ring.y) - gap : visibleBottom - visibleTop;
  const height = Math.min(preferredHeight, Math.max(120, availableHeight));
  const x = vertical ? Math.max(8, Math.min(chartWidth - width - 8, ring.x - width / 2))
    : side === "right" ? ring.x + gap : ring.x - gap - width;
  const y = vertical ? side === "below" ? ring.y + gap : ring.y - gap - height
    : Math.max(visibleTop, Math.min(visibleBottom - height, ring.y - height / 2));
  return {
    x, y, width, height, side,
    ringX: vertical ? ring.x : ring.x + (side === "right" ? LANDSCAPE_CLUSTER_MARKER_BOUND : -LANDSCAPE_CLUSTER_MARKER_BOUND),
    ringY: vertical ? ring.y + (side === "below" ? LANDSCAPE_CLUSTER_MARKER_BOUND : -LANDSCAPE_CLUSTER_MARKER_BOUND) : ring.y,
    edgeX: vertical ? Math.max(x + 16, Math.min(x + width - 16, ring.x)) : side === "right" ? x : x + width,
    edgeY: vertical ? side === "below" ? y : y + height : Math.max(y + 16, Math.min(y + height - 16, ring.y)),
  };
}

export interface LandscapeModelPoint extends LandscapePoint {
  model: ModelView;
  value: number;
  estimated: boolean;
  color: string;
  family: ModelColorFamily | null;
  pricing: EffectivePricing;
  band: PriceBandId;
  maxScore: number;
}

export interface LandscapeLayoutOptions {
  models: readonly ModelView[];
  metric?: ScoreMetric;
  selection?: BenchmarkSelection;
  priceMode: PriceMode;
  activity: PricingActivity;
  costTierVersion: string;
  tierScheme: TierScheme;
  coloringMode: LandscapeColoringMode;
  colorScheme: ModelColorScheme | null;
  companyColors: ReadonlyMap<CompanySlug, string>;
  chartWidth: number;
  scoreBaseline: number;
  scoreSpan: number;
  pointInset?: number;
}

export interface LandscapeLayout {
  points: LandscapeModelPoint[];
  maxScore: number;
  scoreDomain: [number, number];
  scoreTicks: number[];
  tierSlot: number;
}

/** The production score/price-to-SVG calculation shared by the chart and fixtures. */
export function layoutLandscapeModels(options: LandscapeLayoutOptions): LandscapeLayout {
  const pointInset = options.pointInset ?? 18;
  const tierSlot = (options.chartWidth - 12) / options.tierScheme.order.length;
  const pricingFor = (model: ModelView): EffectivePricing =>
    effectivePricing(model, options.priceMode, options.activity, options.costTierVersion);
  const selection = options.selection ?? { source: "artificial-analysis", metric: options.metric ?? "intelligenceIndex" };
  const scored = options.models.map((model) => ({ model, observation: resolveModelScore(model, selection) }))
    .filter((item): item is { model: ModelView; observation: QualifiedModelScore } => item.observation !== null);
  const plotted = scored.filter(({ model }) => priceBandFor(pricingFor(model), options.tierScheme) !== "unknown");
  const values = plotted.map(({ observation }) => observation.value);
  const maxScore = selection.source === "artificial-analysis" ? Math.max(70, Math.ceil(Math.max(...values, 0) / 10) * 10) : Math.max(...values, 1);
  let scoreDomain: [number, number];
  if (selection.source === "artificial-analysis") scoreDomain = [0, maxScore];
  else if (!values.length) scoreDomain = [0, 1];
  else {
    let low = Math.min(...values), high = Math.max(...values);
    if (low === high) { const span = Math.max(1, Math.abs(low) * 0.1); low -= span / 2; high += span / 2; }
    else { const padding = (high - low) * 0.08; low -= padding; high += padding; }
    const rough = (high - low) / 5, magnitude = 10 ** Math.floor(Math.log10(rough));
    const normalized = rough / magnitude, step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
    scoreDomain = [Math.floor(low / step) * step, Math.ceil(high / step) * step];
  }
  const tickStep = selection.source === "artificial-analysis" ? 20 : (scoreDomain[1] - scoreDomain[0]) / 5;
  const scoreTicks = Array.from({ length: 6 }, (_, index) => Math.round((scoreDomain[0] + tickStep * index) * 1e9) / 1e9)
    .filter((tick) => tick >= scoreDomain[0] && tick <= scoreDomain[1]);
  const apexFloor = options.tierScheme.tiers.apex.min;
  const apexMaxPrice = Math.max(apexFloor * 2, ...plotted
    .filter(({ model }) => priceBandFor(pricingFor(model), options.tierScheme) === "apex")
    .map(({ model }) => pricingFor(model).value ?? apexFloor));

  const xFor = (model: ModelView): number => {
    const pricing = pricingFor(model);
    const tier = priceBandFor(pricing, options.tierScheme) as PriceBandId;
    const tierIndex = Math.max(0, options.tierScheme.order.indexOf(tier));
    const bounds = options.tierScheme.tiers[tier];
    const price = pricing.value ?? bounds.min;
    const normalized = bounds.max === null
      ? Math.log(Math.max(price, bounds.min) / bounds.min) / Math.log(apexMaxPrice / bounds.min)
      : (price - bounds.min) / (bounds.max - bounds.min);
    const positioned = Math.pow(Math.max(0, normalized), options.activity.positionPowers?.[tier] ?? 1);
    const clamped = Math.max(0, Math.min(1, Number.isFinite(positioned) ? positioned : 0));
    return 6 + tierIndex * tierSlot + pointInset + clamped * (tierSlot - pointInset * 2);
  };

  return {
    maxScore,
    scoreDomain,
    scoreTicks,
    tierSlot,
    points: plotted.map(({ model, observation }) => {
      const family = options.coloringMode === "name" && options.colorScheme ? modelColorFamily(model) : null;
      const pricing = pricingFor(model);
      return {
        id: model.id,
        model,
        value: observation.value,
        estimated: observation.estimated,
        x: xFor(model),
        y: options.scoreBaseline - ((observation.value - scoreDomain[0]) / (scoreDomain[1] - scoreDomain[0])) * options.scoreSpan,
        color: family?.color ?? options.companyColors.get(model.companySlug) ?? "#fff",
        family,
        pricing,
        band: priceBandFor(pricing, options.tierScheme) as PriceBandId,
        maxScore,
      };
    }),
  };
}

/**
 * Layout a canonical selector result and fail loudly if defensive layout
 * eligibility ever diverges. Count cards and charts both use this contract.
 */
export function layoutCanonicalLandscapeModels(options: LandscapeLayoutOptions): LandscapeLayout {
  const layout = layoutLandscapeModels(options);
  const canonicalIds = options.models.map((model) => model.id);
  const plottedIds = layout.points.map((point) => point.id);
  if (new Set(canonicalIds).size !== canonicalIds.length
    || canonicalIds.length !== plottedIds.length
    || canonicalIds.some((id, index) => id !== plottedIds[index])) {
    throw new Error(`Canonical Landscape eligibility diverged from layout: ${canonicalIds.length} eligible, ${plottedIds.length} plotted.`);
  }
  return layout;
}

export interface LandscapeCluster<Point extends LandscapePoint = LandscapePoint> {
  /** Sorted member IDs joined by an ASCII unit separator. */
  key: string;
  members: Point[];
  x: number;
  y: number;
}

export interface LandscapeClusterMembership {
  key: string;
  memberIds: string[];
}

const CLUSTER_KEY_SEPARATOR = "\u001f";

export const LANDSCAPE_CLUSTER_MARKER_BOUND = 15;
export const LANDSCAPE_RING_POINT_RADIUS = 11;
export const LANDSCAPE_RING_RADIUS = 15;
export const LANDSCAPE_RING_MAX_PERFORMANCE_SPAN = 8;

export interface LandscapeRingClusteringOptions<Point extends LandscapePoint> {
  performanceValue?: (point: Point) => number;
  maxPerformanceSpan?: number;
}

function clusterFor<Point extends LandscapePoint>(members: Point[], key = members.map((member) => member.id).join(CLUSTER_KEY_SEPARATOR)): LandscapeCluster<Point> {
  return {
    key,
    members,
    x: members.reduce((sum, member) => sum + member.x, 0) / members.length,
    y: members.reduce((sum, member) => sum + member.y, 0) / members.length,
  };
}

export function individualLandscapePoints<Point extends LandscapePoint>(
  points: readonly Point[],
): LandscapeCluster<Point>[] {
  return [...points]
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((point) => clusterFor([point]));
}

/**
 * Builds the default Rings-mode glyphs. Visible overlaps merge transitively and
 * generated rings repeatedly absorb touching dots/rings until a fixed point.
 * When performance geometry is supplied, deterministic constrained unions keep
 * bridge points from chaining models across an implausibly large score span.
 */
export function clusterLandscapeRings<Point extends LandscapePoint>(
  points: readonly Point[],
  options: LandscapeRingClusteringOptions<Point> = {},
): LandscapeCluster<Point>[] {
  let groups = [...points]
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((point) => clusterFor([point]));
  const maxPerformanceSpan = options.maxPerformanceSpan ?? Number.POSITIVE_INFINITY;
  const withinPerformanceSpan = (members: readonly Point[]): boolean => {
    if (!options.performanceValue || !Number.isFinite(maxPerformanceSpan)) return true;
    const values = members.map(options.performanceValue);
    return Math.max(...values) - Math.min(...values) <= maxPerformanceSpan;
  };

  while (groups.length > 1) {
    const parents = groups.map((_, index) => index);
    const componentMembers = new Map(groups.map((group, index) => [index, group.members] as const));
    const root = (index: number): number => {
      let current = index;
      while (parents[current] !== current) current = parents[current]!;
      while (parents[index] !== index) {
        const parent = parents[index]!;
        parents[index] = current;
        index = parent;
      }
      return current;
    };
    const union = (left: number, right: number): boolean => {
      const leftRoot = root(left);
      const rightRoot = root(right);
      if (leftRoot === rightRoot) return false;
      const members = [...componentMembers.get(leftRoot)!, ...componentMembers.get(rightRoot)!];
      if (!withinPerformanceSpan(members)) return false;
      const mergedRoot = Math.min(leftRoot, rightRoot);
      const removedRoot = Math.max(leftRoot, rightRoot);
      parents[removedRoot] = mergedRoot;
      componentMembers.set(mergedRoot, members);
      componentMembers.delete(removedRoot);
      return true;
    };

    const touching: Array<{ left: number; right: number; distance: number; key: string }> = [];
    for (let left = 0; left < groups.length; left += 1) {
      for (let right = left + 1; right < groups.length; right += 1) {
        const a = groups[left]!;
        const b = groups[right]!;
        const aRadius = a.members.length > 1 ? LANDSCAPE_RING_RADIUS : LANDSCAPE_RING_POINT_RADIUS;
        const bRadius = b.members.length > 1 ? LANDSCAPE_RING_RADIUS : LANDSCAPE_RING_POINT_RADIUS;
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (distance <= aRadius + bRadius) touching.push({ left, right, distance, key: `${a.key}\u001e${b.key}` });
      }
    }
    touching.sort((left, right) => left.distance - right.distance || left.key.localeCompare(right.key));
    for (const edge of touching) union(edge.left, edge.right);

    const merged = new Map<number, Point[]>();
    for (let index = 0; index < groups.length; index += 1) {
      const members = merged.get(root(index)) ?? [];
      members.push(...groups[index]!.members);
      merged.set(root(index), members);
    }
    const next = [...merged.values()]
      .map((members) => clusterFor(members.sort((left, right) => left.id.localeCompare(right.id))))
      .sort((left, right) => left.key.localeCompare(right.key));
    if (next.length === groups.length) return next;
    groups = next;
  }
  return groups;
}

export function clusterMemberships<Point extends LandscapePoint>(clusters: readonly LandscapeCluster<Point>[]): LandscapeClusterMembership[] {
  return clusters.map((cluster) => ({ key: cluster.key, memberIds: cluster.members.map((member) => member.id) }));
}

/**
 * Applies an earlier membership partition to current point geometry. New
 * points remain independently selectable until the new partition is installed;
 * removed points simply disappear. This lets hit targets share point motion.
 */
export function materializeClusterMemberships<Point extends LandscapePoint>(
  memberships: readonly LandscapeClusterMembership[],
  points: readonly Point[],
): LandscapeCluster<Point>[] {
  const byId = new Map(points.map((point) => [point.id, point] as const));
  const assigned = new Set<string>();
  const materialized: LandscapeCluster<Point>[] = [];
  for (const membership of memberships) {
    const members = membership.memberIds.flatMap((id) => {
      const point = byId.get(id);
      if (!point || assigned.has(id)) return [];
      assigned.add(id);
      return [point];
    });
    if (members.length) materialized.push(clusterFor(members, membership.key));
  }
  for (const point of [...points].sort((left, right) => left.id.localeCompare(right.id))) {
    if (!assigned.has(point.id)) materialized.push(clusterFor([point]));
  }
  return materialized.sort((left, right) => left.key.localeCompare(right.key));
}
